/**
 * name-lead (2026-11-02 · Node 「현장명이 두각 나면 그 이름으로 자연 노출」) — 대표 이름 판정(순수 · 테스트).
 *
 * 한 현장에는 이름이 둘 있는 경우가 많다 — 브랜드 단지명(「힐스테이트 루센츠」)과 구역명(「반여3-1구역」).
 *   사람들이 실제로 더 많이 치는 이름을 제목·h1·구조화 데이터의 «앞» 에 세운다. 다른 이름은 괄호·alternateName 으로 남는다.
 *   판정 재료 = 네이버 검색광고 실측 월간 검색량(site_kick_keywords · 수식어 없는 이름 행). 지어낸 이름 0 — 두 이름 모두 원장에 쓰여 있을 때만.
 *
 * 문턱(흔들림 방지):
 *   · 도전 이름 ≥ 현재 이름 × 1.3 · ≥ 50 · 차이 ≥ 30 을 «서로 다른 측정 두 번» 연속 → 전환.
 *   · 급증(≥ × 2 · ≥ 200)은 한 번에 전환.
 *   · 네이버 상위 20 안 키워드가 있는 페이지는 전환을 보류(hold=rank) — 이미 잡은 순위를 흔들지 않는다.
 *   · 처음 판정(상태 없음)은 문턱(1.3배 · 50 · 차이 30)만 넘으면 바로 자리 잡는다.
 *   · 되돌림 뒤 30일은 다시 바꾸지 않는다(cooldown).
 *   · 청약 공고가 붙은 분양 현장은 공고 단지명이 앞(구역명 앞세우기 없음 · hold=notice).
 */
import { normalizeSiteName } from '@/lib/cl-core/names';
import { BRAND_WORDS } from '@/lib/cl-core/brands';

export type NameLead = 'brand' | 'zone';

export const LEAD_RATIO = 1.3;
export const LEAD_MIN_VOL = 50;
export const LEAD_MIN_GAP = 30;
export const LEAD_STREAK = 2;
export const SURGE_RATIO = 2;
export const SURGE_MIN_VOL = 200;
export const LEAD_COOLDOWN_DAYS = 30;
/** 되돌림 판정: 전환 뒤 이 수 이상 키워드가 5위 넘게 밀리면 되돌린다. */
export const REVERT_MIN_KEYWORDS = 2;
export const REVERT_DROP = 5;
export const REVERT_WINDOW_DAYS = 14;

const sq = (s: string | null | undefined) => String(s ?? '').replace(/\s+/g, '').toLowerCase();
const cl = (s: string | null | undefined) => normalizeSiteName(String(s ?? '')).replace(/\s+/g, ' ').trim();

/** 브랜드가 아닌 일반·지역·구역 표기. */
export const GENERIC_NAME_RE = /(공동주택|구역|지구|재개발|재건축|정비|도시개발|조합|번지|일원|블록|\d+\s*BL\b|BL$|가칭|\d)/i;

export interface NameLeadSiteInput {
  name: string | null;
  display_name?: string | null;
  site_type?: string | null;
  sigungu?: string | null;
  region?: string | null;
  name_variants?: unknown;
  /** 정비 원장 구역명(「반여3-1 재건축」) */
  redevDistrict?: string | null;
}

/** 「반여3-1 재건축」·「반여3-1구역」·「시민공원 촉진2-1구역」 → 「반여3-1구역」 꼴. 구역이 아니면 null. */
export function zoneLabelOf(raw: string | null | undefined, sigungu?: string | null): string | null {
  let s = cl(raw).replace(/\((?:[^()]*)\)/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  // 앞머리 지역어(시도·시군구) 떼기
  const toks = s.split(' ');
  const sg = cl(sigungu);
  while (toks.length > 1 && (/^[가-힣]{1,4}(특별시|광역시|특별자치시|특별자치도|도|시|군|구)$/.test(toks[0]) || toks[0] === sg || /^(서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충북|충남|전북|전남|경북|경남|제주)$/.test(toks[0]))) toks.shift();
  s = toks.join(' ').replace(/\s*(주택)?(재개발|재건축)(정비사업)?$/, '').trim();
  if (!s || /입구역$/.test(s)) return null; // 「홍대입구역」 같은 역명
  if (/구역$/.test(s)) return s.replace(/\s+/g, '');
  // 재건축 단지명(「신반포22차 재건축」→「신반포22차」) — 사람들이 옛 아파트 이름으로 찾는다.
  if (/\d+차$/.test(s) && /[가-힣]/.test(s)) return s.replace(/\s+/g, '');
  if (/[0-9A-Za-z]$/.test(s) && /[가-힣]/.test(s)) return `${s.replace(/\s+/g, '')}구역`;
  return null;
}

/** 이름 후보: 브랜드(단지명) · 구역. 둘 다 있을 때만 판정 대상. */
/**
 * 구역의 «옛 단지명» 별칭 — 정비 원장 구역명 괄호(「남천2-3(삼익비치)」·「세류1(미영아파트)」)에서.
 *   사람들은 구역 번호보다 옛 아파트 이름(삼익비치)으로 더 많이 찾는 경우가 많다 → 구역 쪽 검색량에 함께 넣는다.
 *   일반어(소규모·도시정비형 재개발·지구 등)는 별칭이 아니다.
 */
export function zoneAliasesOf(...srcs: Array<string | null | undefined>): string[] {
  const out = new Set<string>();
  for (const src of srcs) {
    for (const m of String(src ?? '').matchAll(/[(（]([^()（）]+)[)）]/g)) {
      for (const part of m[1].split(/[,·]/)) {
        const a = cl(part);
        if (a.length < 2 || !/[가-힣]/.test(a) || /구역$/.test(a)) continue;
        if (/(재개발|재건축|정비|소규모|사업|지구|집결지|역세권|촉진|도시환경)/.test(a)) continue;
        // 지역 이름(「(수원)」·「(해운대구)」)은 별칭이 아니다 — 11/02 예행 실측 「수원」 월 51,800 이 구역 쪽 검색량으로 잡힐 뻔했다.
        if (/^(서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충북|충남|전북|전남|경북|경남|제주)$/.test(a) || /^[가-힣]{1,5}(시|군|구|동|읍|면)$/.test(a) || a.length <= 2) continue;
        // 브랜드 단지명(「올림픽파크포레온」처럼 괄호에 새 이름을 적은 경우)은 구역 쪽이 아니다.
        if (BRAND_WORDS.some((b) => sq(a).includes(sq(b)))) continue;
        out.add(a);
        const bare = a.replace(/\s*아파트$/, '').trim();
        if (bare !== a && bare.length >= 2) out.add(bare);
      }
    }
  }
  return [...out];
}

export function nameLeadCandidates(site: NameLeadSiteInput): { brand: string | null; zone: string | null } {
  const name = cl(site.name);
  const disp = cl(site.display_name);
  const paren = /^(.*?)\s*[(（]([^()（）]+)[)）]\s*$/.exec(name);
  let brand: string | null = null;
  let zone: string | null = null;
  if (paren && /구역$/.test(paren[2].trim())) {
    if (!GENERIC_NAME_RE.test(paren[1])) brand = paren[1].trim();
    zone = zoneLabelOf(paren[2], site.sigungu);
  }
  const dash = /^(.+?)\s+[—–]\s+(.+)$/.exec(disp);
  if (!brand && dash && !GENERIC_NAME_RE.test(dash[1])) brand = dash[1].trim();
  if (!brand && site.site_type !== 'redevelopment' && name && !GENERIC_NAME_RE.test(name)) brand = name;
  if (!zone) zone = zoneLabelOf(site.redevDistrict, site.sigungu);
  if (!zone && site.site_type === 'redevelopment') zone = zoneLabelOf(name, site.sigungu);
  if (!zone) {
    const vars = Array.isArray(site.name_variants) ? (site.name_variants as unknown[]).map((v) => cl(String(v ?? ''))) : [];
    for (const v of vars) {
      if (!(/구역$/.test(v) || /\d+차(\s*(재건축|아파트))?$/.test(v)) || /입구역$/.test(v)) continue;
      const z = zoneLabelOf(v, site.sigungu);
      if (z && z.length >= 4) { zone = z; break; }
    }
  }
  if (brand && zone && sq(brand).includes(sq(zone).replace(/구역$/, ''))) brand = null; // 브랜드가 구역명을 품으면 같은 이름
  return { brand, zone };
}

export interface KickNameRow { keyword: string; modifier: string | null; volume_total: number | null; is_lt10?: boolean | null; checked_at?: string | null }

/** 접두 지역어(「청주」「의왕시」)만 붙은 같은 이름인가. */
function localPrefixed(kw: string, target: string, locs: readonly string[]): boolean {
  if (!kw.endsWith(target) || kw === target) return false;
  const pre = kw.slice(0, kw.length - target.length);
  return locs.some((l) => l && l === pre);
}

/** 지역 접두어 후보(시도·시군구 · 끝 시/군/구 뗀 꼴). */
export function localityPrefixes(site: Pick<NameLeadSiteInput, 'region' | 'sigungu'>): string[] {
  const out = new Set<string>();
  const add = (s: string) => { const n = sq(s); if (n) out.add(n); };
  const r = cl(site.region);
  if (r) { add(r); add(`${r}시`); }
  const sg = cl(site.sigungu);
  for (const part of sg.split(' ')) {
    if (!part) continue;
    add(part);
    add(part.replace(/(시|군|구)$/, ''));
  }
  return [...out].filter((x) => x.length >= 2);
}

/** 이름 하나의 실측 검색량 — 수식어 없는 행 중 그 이름(또는 지역 접두 꼴)의 최댓값. 측정이 없으면 null. */
export function volumeOfName(rows: readonly KickNameRow[], name: string | null, locs: readonly string[]): { vol: number; at: string | null } | null {
  if (!name) return null;
  const t = sq(name);
  if (t.length < 2) return null;
  let best: { vol: number; at: string | null } | null = null;
  for (const r of rows) {
    if (r.modifier) continue;
    const k = sq(r.keyword);
    if (k !== t && !localPrefixed(k, t, locs)) continue;
    const v = r.is_lt10 ? 0 : Math.max(0, Number(r.volume_total) || 0);
    if (!best || v > best.vol) best = { vol: v, at: r.checked_at ?? null };
    else if (best && r.checked_at && (!best.at || r.checked_at > best.at) && v === best.vol) best.at = r.checked_at;
  }
  return best;
}

export interface LeadState {
  lead: NameLead;
  challenger_streak: number;
  measured_at: string | null;
  cooldown_until: string | null;
}

export interface LeadDecision {
  lead: NameLead;
  default_lead: NameLead;
  challenger_streak: number;
  flipped: boolean;
  reason: 'hysteresis' | 'surge' | 'initial' | null;
  hold_reason: 'rank' | 'cooldown' | 'notice' | null;
}

/** 기본 앞 이름 — 정비 단계(구역으로 찾는 때)는 구역 · 그 밖(분양·공사·입주)은 단지명. */
export function defaultLeadOf(siteType: string | null | undefined, redevStage: boolean): NameLead {
  return siteType === 'redevelopment' && redevStage ? 'zone' : 'brand';
}

/**
 * 판정(순수). prev 가 없으면 기본 앞 이름에서 출발한다.
 *   measuredAt 이 prev.measured_at 과 같으면(새 측정 없음) 연속 수를 올리지 않는다.
 *   rankHeld = 이 페이지에 네이버 상위 20 키워드가 있다 → 전환하지 않는다(문턱 넘김 기록만).
 */
export function decideLead(i: {
  prev: LeadState | null;
  defaultLead: NameLead;
  brandVol: number;
  zoneVol: number;
  measuredAt: string | null;
  rankHeld: boolean;
  nowMs: number;
  /** 청약 공고가 붙은(또는 청약·잔여 국면) 분양 현장 — 공고 단지명이 정식 이름이라 구역명을 앞세우지 않는다. */
  noticeLocked?: boolean;
}): LeadDecision {
  const cur: NameLead = i.prev?.lead ?? i.defaultLead;
  const other: NameLead = cur === 'brand' ? 'zone' : 'brand';
  const curVol = cur === 'brand' ? i.brandVol : i.zoneVol;
  const chVol = other === 'brand' ? i.brandVol : i.zoneVol;
  const wins = chVol >= LEAD_MIN_VOL && chVol - curVol >= LEAD_MIN_GAP && chVol >= curVol * LEAD_RATIO;
  const surge = chVol >= SURGE_MIN_VOL && chVol >= curVol * SURGE_RATIO;
  const newMeasure = !i.prev || !i.prev.measured_at || (!!i.measuredAt && i.measuredAt > i.prev.measured_at);
  const prevStreak = i.prev?.challenger_streak ?? 0;
  const streak = wins ? (newMeasure ? prevStreak + 1 : Math.max(prevStreak, 1)) : 0;
  const base = { default_lead: i.defaultLead, challenger_streak: streak };
  const cooling = !!i.prev?.cooldown_until && Date.parse(i.prev.cooldown_until) > i.nowMs;
  if (!wins) return { ...base, lead: cur, flipped: false, reason: null, hold_reason: cooling ? 'cooldown' : null };
  // 첫 판정(이전 상태 없음)은 «처음 자리 잡기» — 흔들림 문턱(두 번 연속)은 이미 정한 앞 이름을 바꿀 때만 건다.
  const ready = surge || streak >= LEAD_STREAK || !i.prev;
  if (!ready) return { ...base, lead: cur, flipped: false, reason: null, hold_reason: null };
  if (cooling) return { ...base, lead: cur, flipped: false, reason: null, hold_reason: 'cooldown' };
  if (i.rankHeld) return { ...base, lead: cur, flipped: false, reason: null, hold_reason: 'rank' };
  if (i.noticeLocked && other === 'zone') return { ...base, lead: cur, flipped: false, reason: null, hold_reason: 'notice' };
  return { ...base, lead: other, challenger_streak: 0, flipped: true, reason: !i.prev ? 'initial' : surge && streak < LEAD_STREAK ? 'surge' : 'hysteresis', hold_reason: null };
}

/** 되돌림 판정 — 전환 전 최고 순위 대비 지금(최근 3일 최고) 순위가 REVERT_DROP 넘게 밀린(또는 빠진) 키워드 수. */
export function rankDropCount(before: Record<string, number>, after: Record<string, number | undefined>): number {
  let n = 0;
  for (const [kw, b] of Object.entries(before)) {
    if (!(b > 0) || b > 30) continue;
    const a = after[kw];
    if (a === undefined || a - b > REVERT_DROP) n++;
  }
  return n;
}

/** apt_sites.name_lead 확정값(화면이 읽는 꼴). */
export interface NameLeadValue { lead: NameLead; brand: string | null; zone: string | null; brand_vol?: number | null; zone_vol?: number | null; at?: string | null }

/** 화면용 — 확정값을 읽는다(꼴이 틀리면 null = 종전 규칙). */
export function parseNameLead(v: unknown): NameLeadValue | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.lead !== 'brand' && o.lead !== 'zone') return null;
  const s = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  return { lead: o.lead, brand: s(o.brand), zone: s(o.zone), brand_vol: Number(o.brand_vol) || null, zone_vol: Number(o.zone_vol) || null, at: s(o.at) };
}

/**
 * 콘텐츠(블로그·카페·게시판·허브)가 쓸 «앞 이름» — 확정값이 브랜드면 브랜드, 구역이면 구역, 없으면 fallback.
 *   제목에 두 이름을 함께 쓸 땐 leadPair 를 쓴다(「힐스테이트 루센츠(반여3-1구역)」).
 */
export function leadNameOf(site: { name_lead?: unknown } | null | undefined, fallback: string): string {
  const v = parseNameLead(site?.name_lead);
  if (!v) return fallback;
  return (v.lead === 'brand' ? v.brand : v.zone) ?? fallback;
}

export function leadPair(site: { name_lead?: unknown } | null | undefined): string | null {
  const v = parseNameLead(site?.name_lead);
  if (!v || !v.brand || !v.zone) return null;
  return v.lead === 'brand' ? `${v.brand}(${v.zone})` : `${v.zone}(${v.brand})`;
}

/* ───────── 같은 사업이 두 장(옛 구역·가칭 페이지 + 새 단지명 페이지)으로 갈린 경우 ───────── */

export interface DupSite { id: string; slug: string; name: string; region: string | null; sigungu: string | null; total_units: number | null; created_at: string; name_variants?: unknown }
export interface DupEvidence { site_id: string; proposed_name: string; outlet: string | null }
export interface DupMerge { from: DupSite; to: DupSite; outlets: number }

/**
 * 뉴스가 «옛 페이지(구역·가칭)의 새 단지명» 이라고 여러 매체에서 말한 이름이 이미 다른 활성 현장의 이름이면, 그 둘은 같은 사업이다.
 *   안전 문턱: 매체 2곳 이상 · 시도·시군구 같음 · 세대수가 둘 다 있으면 같아야(없으면 매체 3곳 이상) · 새 페이지가 더 늦게 생김.
 *   (실측 오탐: 「돈의문2재정비촉진구역」 ← 「화성향남 롯데캐슬 시그니처」 매체 3곳 — 시군구가 달라 걸러진다.)
 */
export function findDuplicateMerges(sites: readonly DupSite[], ev: readonly DupEvidence[]): DupMerge[] {
  const byId = new Map(sites.map((x) => [x.id, x]));
  const byName = new Map<string, DupSite[]>();
  for (const x of sites) { const k = sq(x.name); const a = byName.get(k) ?? []; a.push(x); byName.set(k, a); }
  const votes = new Map<string, Set<string>>();
  for (const e of ev) {
    const k = `${e.site_id}|${sq(e.proposed_name)}`;
    const v = votes.get(k) ?? new Set<string>();
    if (e.outlet) v.add(e.outlet);
    votes.set(k, v);
  }
  const out: DupMerge[] = [];
  const used = new Set<string>();
  for (const [k, outlets] of votes) {
    const [sid, pn] = k.split('|');
    const from = byId.get(sid);
    if (!from || used.has(from.id) || outlets.size < 2) continue;
    for (const to of byName.get(pn) ?? []) {
      if (to.id === from.id) continue;
      if (!from.region || from.region !== to.region || !from.sigungu || from.sigungu !== to.sigungu) continue;
      const fu = Number(from.total_units) || 0, tu = Number(to.total_units) || 0;
      if (fu && tu ? fu !== tu : outlets.size < 3) continue;
      if (Date.parse(to.created_at) < Date.parse(from.created_at)) continue;
      out.push({ from, to, outlets: outlets.size });
      used.add(from.id);
      break;
    }
  }
  return out;
}
